import { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Inbox, MailCheck, Building2, Radar, KanbanSquare,
  Settings, LogOut, ChevronLeft, ChevronRight, MoreHorizontal, Users, Target, BookOpen, ShieldCheck, Scale, Award, PenSquare, Search, Sun, Moon,
  ChevronUp, ChevronDown, GripVertical, ListOrdered, RotateCcw, Eye, EyeOff, Send,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { FocusProvider } from "@/context/FocusContext";
import { FocusMenu } from "@/components/focus/FocusMenu";
import { CommandPalette, openCommandPalette } from "@/components/common/CommandPalette";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "sidebar-nav-dashboard" },
  { to: "/inbox", label: "Inbox", icon: Inbox, testId: "sidebar-nav-inbox" },
  { to: "/contacts", label: "Contacts", icon: Users, testId: "sidebar-nav-contacts" },
  { to: "/receipts", label: "Read Receipts", icon: MailCheck, testId: "sidebar-nav-receipts" },
  { to: "/intelligence", label: "Intelligence", icon: Building2, testId: "sidebar-nav-intelligence" },
  { to: "/pipeline", label: "Pipeline", icon: Target, testId: "sidebar-nav-pipeline" },
  { to: "/signals", label: "Signals", icon: Radar, testId: "sidebar-nav-signals" },
  { to: "/content", label: "Content", icon: PenSquare, testId: "sidebar-nav-content" },
  { to: "/ledger", label: "Ledger", icon: Scale, testId: "sidebar-nav-ledger" },
  { to: "/projects", label: "Projects", icon: KanbanSquare, testId: "sidebar-nav-projects" },
  { to: "/review", label: "Weekly Review", icon: BookOpen, testId: "sidebar-nav-review" },
  { to: "/scorecard", label: "Scorecard", icon: Award, testId: "sidebar-nav-scorecard" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "sidebar-nav-settings" },
];

const MOBILE_PRIMARY_PATHS = ["/dashboard", "/inbox", "/pipeline", "/projects"];

const Wordmark = ({ compact = false }) => (
  <span className="flex items-center gap-2">
    <span className="w-2 h-2 bg-[hsl(var(--editorial-red))] shrink-0" aria-hidden="true" />
    {!compact && <span className="font-heading font-semibold tracking-tight text-[15px]">Discern</span>}
  </span>
);

export const AppShell = () => (
  <FocusProvider>
    <AppShellInner />
  </FocusProvider>
);

const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className="flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:bg-secondary transition-colors"
      data-testid="theme-toggle"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Light mode" : "Dark mode"}
    >
      {isDark ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
    </button>
  );
};

const AppShellInner = () => {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [duePosts, setDuePosts] = useState(0);
  const [gmailReconnect, setGmailReconnect] = useState(0);
  const [accessPending, setAccessPending] = useState(0);
  const [fwdAlerts, setFwdAlerts] = useState(0);
  const [reordering, setReordering] = useState(false);
  const [order, setOrder] = useState(null); // saved array of nav `to` paths, or null for default
  const [hidden, setHidden] = useState([]); // nav `to` paths the user has hidden
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragIndex = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Global mail search lives in the header. Typing + Enter opens the full results view (/search),
  // which searches every connected account. Cmd/Ctrl+K still opens the command palette separately.
  const [headerQuery, setHeaderQuery] = useState("");
  const goSearch = (e) => {
    if (e) e.preventDefault();
    const q = headerQuery.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}&mode=keyword` : "/search");
  };

  // Persisted per-user menu order + hidden set (user tailors the sidebar to how they work).
  const orderKey = user?.user_id ? `discern:navOrder:${user.user_id}` : "discern:navOrder";
  const hiddenKey = user?.user_id ? `discern:navHidden:${user.user_id}` : "discern:navHidden";
  useEffect(() => {
    try {
      const raw = localStorage.getItem(orderKey);
      setOrder(raw ? JSON.parse(raw) : null);
    } catch (e) { setOrder(null); }
    try {
      const rawH = localStorage.getItem(hiddenKey);
      setHidden(rawH ? JSON.parse(rawH) : []);
    } catch (e) { setHidden([]); }
  }, [orderKey, hiddenKey]);

  // Reset scroll to the top whenever the route changes so long pages never open mid-scroll.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Keep a live unread count for the Inbox nav badge: poll gently + refresh on demand
  // (Inbox dispatches "discern:unread" after read/archive/sync actions).
  const fetchUnread = useCallback(async () => {
    try {
      const r = await api.get("/emails/unread-count");
      setUnread(r.data?.unread || 0);
    } catch (e) { /* non-critical */ }
  }, []);
  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 45000);
    const onEvt = () => fetchUnread();
    window.addEventListener("discern:unread", onEvt);
    return () => { clearInterval(id); window.removeEventListener("discern:unread", onEvt); };
  }, [fetchUnread]);

  // Live "due now" count for the Content nav badge: scheduled posts whose reminder time has arrived.
  const fetchDuePosts = useCallback(async () => {
    try {
      const r = await api.get("/content/schedule/due-count");
      setDuePosts(r.data?.due || 0);
    } catch (e) { /* non-critical */ }
  }, []);
  useEffect(() => {
    fetchDuePosts();
    const id = setInterval(fetchDuePosts, 60000);
    const onEvt = () => fetchDuePosts();
    window.addEventListener("discern:scheduled", onEvt);
    return () => { clearInterval(id); window.removeEventListener("discern:scheduled", onEvt); };
  }, [fetchDuePosts]);

  // Gmail health: red dot on Settings when any connected mailbox needs reconnecting.
  const fetchGmailHealth = useCallback(async () => {
    try {
      const r = await api.get("/gmail/reconnect-count");
      setGmailReconnect(r.data?.count || 0);
    } catch (e) { /* non-critical */ }
  }, []);
  useEffect(() => {
    fetchGmailHealth();
    const id = setInterval(fetchGmailHealth, 120000);
    const onEvt = () => fetchGmailHealth();
    window.addEventListener("discern:gmail", onEvt);
    return () => { clearInterval(id); window.removeEventListener("discern:gmail", onEvt); };
  }, [fetchGmailHealth]);

  // Owner-only: pending access requests drive a badge on the Access nav so the owner is notified
  // in-app the moment someone asks to join (a best-effort email is also sent server-side).
  const fetchAccessPending = useCallback(async () => {
    if (!user?.is_owner) return;
    try {
      const r = await api.get("/admin/access/pending-count");
      setAccessPending(r.data?.pending || 0);
    } catch (e) { /* non-critical */ }
  }, [user?.is_owner]);
  useEffect(() => {
    fetchAccessPending();
    const id = setInterval(fetchAccessPending, 60000);
    const onEvt = () => fetchAccessPending();
    window.addEventListener("discern:access", onEvt);
    return () => { clearInterval(id); window.removeEventListener("discern:access", onEvt); };
  }, [fetchAccessPending]);

  // Read Receipts: badge + toast when a tracked email first looks forwarded (opt-in via
  // Receipts > Forward alerts). Visiting /receipts clears these via "discern:fwd-alerts".
  const fwdSeenRef = useRef(null);
  const fetchFwdAlerts = useCallback(async () => {
    try {
      const r = await api.get("/receipts/alerts");
      const count = r.data?.count || 0;
      const items = r.data?.items || [];
      if (fwdSeenRef.current !== null && count > fwdSeenRef.current && items.length) {
        const latest = items[0];
        toast.warning("A tracked email may have been forwarded", {
          description: latest.subject ? `"${latest.subject}"` : latest.to,
        });
      }
      fwdSeenRef.current = count;
      setFwdAlerts(count);
    } catch (e) { /* non-critical */ }
  }, []);
  useEffect(() => {
    fetchFwdAlerts();
    const id = setInterval(fetchFwdAlerts, 60000);
    const onEvt = () => fetchFwdAlerts();
    window.addEventListener("discern:fwd-alerts", onEvt);
    return () => { clearInterval(id); window.removeEventListener("discern:fwd-alerts", onEvt); };
  }, [fetchFwdAlerts]);

  const countBadge = (count, testId, extra = "") =>
    count > 0 ? (
      <span
        className={cn(
          "min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--editorial-red))] text-white text-[9px] font-semibold flex items-center justify-center leading-none",
          extra
        )}
        data-testid={testId}
      >
        {count > 99 ? "99+" : count}
      </span>
    ) : null;

  const inboxBadge = (extra = "") => countBadge(unread, "nav-inbox-unread", extra);
  const contentBadge = (extra = "") => countBadge(duePosts, "nav-content-due", extra);
  const settingsBadge = (extra = "") => countBadge(gmailReconnect, "nav-settings-reconnect", extra);
  const accessBadge = (extra = "") => countBadge(accessPending, "nav-access-pending", extra);
  const receiptsBadge = (extra = "") => countBadge(fwdAlerts, "nav-receipts-forwarded", extra);

  const navItems = user?.is_owner
    ? [
        ...NAV_ITEMS.slice(0, -1),
        { to: "/admin/access", label: "Access", icon: ShieldCheck, testId: "sidebar-nav-access" },
        { to: "/sentinel", label: "Sentinel", icon: Send, testId: "sidebar-nav-sentinel" },
        NAV_ITEMS[NAV_ITEMS.length - 1],
      ]
    : NAV_ITEMS;

  // Apply the user's saved order; any items not in the saved list (new features, owner-only
  // Access) fall back to their default position so nothing ever disappears.
  const orderNav = (items) => {
    if (!order || !order.length) return items;
    const byPath = Object.fromEntries(items.map((n) => [n.to, n]));
    const seen = new Set();
    const result = [];
    order.forEach((to) => { if (byPath[to]) { result.push(byPath[to]); seen.add(to); } });
    items.forEach((n) => { if (!seen.has(n.to)) result.push(n); });
    return result;
  };
  const orderedNavItems = orderNav(navItems);

  const persistOrder = (items) => {
    const paths = items.map((n) => n.to);
    setOrder(paths);
    try { localStorage.setItem(orderKey, JSON.stringify(paths)); } catch (e) { /* ignore */ }
  };
  const moveNavItem = (index, dir) => {
    const items = [...orderedNavItems];
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    [items[index], items[j]] = [items[j], items[index]];
    persistOrder(items);
  };
  const resetNavOrder = () => {
    setOrder(null);
    setHidden([]);
    try { localStorage.removeItem(orderKey); } catch (e) { /* ignore */ }
    try { localStorage.removeItem(hiddenKey); } catch (e) { /* ignore */ }
  };

  const persistHidden = (list) => {
    setHidden(list);
    try { localStorage.setItem(hiddenKey, JSON.stringify(list)); } catch (e) { /* ignore */ }
  };
  const toggleHidden = (to) => {
    persistHidden(hidden.includes(to) ? hidden.filter((x) => x !== to) : [...hidden, to]);
  };

  // Drag-and-drop reordering (native HTML5). Up/down buttons remain for keyboard/a11y.
  const onDragStartRow = (index) => { dragIndex.current = index; };
  const onDragOverRow = (e, index) => { e.preventDefault(); if (dragOverIdx !== index) setDragOverIdx(index); };
  const onDropRow = (index) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    setDragOverIdx(null);
    if (from == null || from === index) return;
    const items = [...orderedNavItems];
    const [moved] = items.splice(from, 1);
    items.splice(index, 0, moved);
    persistOrder(items);
  };
  const onDragEndRow = () => { dragIndex.current = null; setDragOverIdx(null); };

  // Items actually shown in the sidebar/nav (hidden ones drop out unless we're customizing).
  const visibleNavItems = orderedNavItems.filter((n) => !hidden.includes(n.to));

  const mobilePrimary = visibleNavItems.filter((n) => MOBILE_PRIMARY_PATHS.includes(n.to));
  const mobileMore = visibleNavItems.filter((n) => !MOBILE_PRIMARY_PATHS.includes(n.to));

  return (
    <div className="min-h-screen bg-background flex paper-grain">
      <CommandPalette />
      {/* Desktop sidebar */}
      <aside
        className={cn(
          "hidden md:flex flex-col border-r border-border bg-card sticky top-0 h-screen transition-[width] duration-200",
          collapsed ? "w-[72px]" : "w-[250px]"
        )}
      >
        <div className="flex items-center gap-2.5 px-4 h-16 border-b border-border">
          <Wordmark compact={collapsed} />
        </div>
        <nav className="flex-1 py-4 px-2.5 space-y-1 overflow-y-auto">
          {(reordering && !collapsed ? orderedNavItems : visibleNavItems).map((item, idx) =>
            reordering && !collapsed ? (
              <div
                key={item.to}
                draggable
                onDragStart={() => onDragStartRow(idx)}
                onDragOver={(e) => onDragOverRow(e, idx)}
                onDrop={() => onDropRow(idx)}
                onDragEnd={onDragEndRow}
                className={cn(
                  "flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium border border-dashed bg-muted/40 cursor-grab active:cursor-grabbing transition-colors",
                  dragOverIdx === idx ? "border-accent ring-1 ring-accent" : "border-border",
                  hidden.includes(item.to) && "opacity-45"
                )}
                data-testid={`sidebar-reorder-row-${item.testId}`}
              >
                <GripVertical className="w-4 h-4 shrink-0 text-muted-foreground/60" aria-hidden="true" />
                <item.icon className="w-[18px] h-[18px] shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{item.label}</span>
                <button
                  type="button"
                  onClick={() => toggleHidden(item.to)}
                  aria-label={hidden.includes(item.to) ? `Show ${item.label}` : `Hide ${item.label}`}
                  data-testid={`nav-hide-${item.testId}`}
                  className="flex items-center justify-center w-6 h-6 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                  title={hidden.includes(item.to) ? "Show in menu" : "Hide from menu"}
                >
                  {hidden.includes(item.to) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => moveNavItem(idx, -1)}
                  disabled={idx === 0}
                  aria-label={`Move ${item.label} up`}
                  data-testid={`nav-move-up-${item.testId}`}
                  className="flex items-center justify-center w-6 h-6 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30 disabled:pointer-events-none transition-colors"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveNavItem(idx, 1)}
                  disabled={idx === orderedNavItems.length - 1}
                  aria-label={`Move ${item.label} down`}
                  data-testid={`nav-move-down-${item.testId}`}
                  className="flex items-center justify-center w-6 h-6 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30 disabled:pointer-events-none transition-colors"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  cn(
                    "relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-secondary text-foreground border border-border font-semibold"
                      : "text-muted-foreground hover:bg-muted/70 hover:text-foreground border border-transparent"
                  )
                }
              >
                <item.icon className="w-[18px] h-[18px] shrink-0" />
                {!collapsed && <span>{item.label}</span>}
                {item.to === "/inbox" && inboxBadge(collapsed ? "absolute top-1 right-1" : "ml-auto")}
                {item.to === "/content" && contentBadge(collapsed ? "absolute top-1 right-1" : "ml-auto")}
                {item.to === "/settings" && settingsBadge(collapsed ? "absolute top-1 right-1" : "ml-auto")}
                {item.to === "/receipts" && receiptsBadge(collapsed ? "absolute top-1 right-1" : "ml-auto")}
                {item.to === "/admin/access" && accessBadge(collapsed ? "absolute top-1 right-1" : "ml-auto")}
              </NavLink>
            )
          )}
        </nav>
        <div className="p-2.5 border-t border-border space-y-1">
          {!collapsed && (
            reordering ? (
              <>
                <p className="px-1 pb-1 text-[11px] leading-snug text-muted-foreground" data-testid="reorder-hint">
                  Drag to reorder or use the arrows. Tap the eye to hide an item (still reachable from search).
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-center text-xs text-muted-foreground gap-1.5"
                    onClick={resetNavOrder}
                    data-testid="sidebar-reorder-reset"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Reset
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1 justify-center text-xs"
                    onClick={() => setReordering(false)}
                    data-testid="sidebar-reorder-done"
                  >
                    Done
                  </Button>
                </div>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs text-muted-foreground gap-2"
                onClick={() => setReordering(true)}
                data-testid="sidebar-reorder-toggle"
              >
                <ListOrdered className="w-4 h-4" /> Customize menu
              </Button>
            )
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-muted-foreground"
            onClick={() => { setCollapsed(!collapsed); setReordering(false); }}
            data-testid="sidebar-collapse-button"
            aria-label="Toggle sidebar"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2 md:hidden">
            <Wordmark />
          </div>
          <div className="hidden md:flex items-center gap-2 flex-1 max-w-md">
            <form onSubmit={goSearch} className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={headerQuery}
                onChange={(e) => setHeaderQuery(e.target.value)}
                placeholder="Search all mail..."
                className="pl-9 h-9 bg-card"
                data-testid="global-search-input"
                aria-label="Search all connected mail accounts"
              />
            </form>
            <button
              onClick={openCommandPalette}
              className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 h-9 text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors shrink-0"
              data-testid="command-trigger"
              aria-label="Open command palette"
              title="Command palette"
            >
              <kbd className="inline-flex items-center gap-0.5 font-mono text-[10px]">⌘K</kbd>
            </button>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => navigate("/search")}
              className="md:hidden flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:bg-secondary transition-colors"
              data-testid="global-search-trigger-mobile"
              aria-label="Search all mail"
            >
              <Search className="w-[18px] h-[18px]" />
            </button>
            <ThemeToggle />
            <FocusMenu />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 rounded-full hover:bg-secondary px-2 py-1.5 transition-colors" data-testid="user-menu-button">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium max-w-[140px] truncate">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-menu-settings">
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 pb-[calc(5rem_+_env(safe-area-inset-bottom))] md:pb-0 min-w-0">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border flex items-stretch pb-[env(safe-area-inset-bottom)]">
        {mobilePrimary.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              cn(
                "relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium",
                isActive ? "text-accent" : "text-muted-foreground"
              )
            }
          >
            <span className="relative">
              <item.icon className="w-5 h-5" />
              {item.to === "/inbox" && inboxBadge("absolute -top-1.5 -right-2.5")}
            </span>
            {item.label.split(" ")[0]}
          </NavLink>
        ))}
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <button className="relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium text-muted-foreground" data-testid="mobile-nav-more">
              <span className="relative">
                <MoreHorizontal className="w-5 h-5" />
                {contentBadge("absolute -top-1.5 -right-2.5")}
              </span>
              More
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="bg-card">
            <div className="grid grid-cols-3 gap-3 pt-4">
              {mobileMore.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMoreOpen(false)}
                  className="relative flex flex-col items-center gap-2 rounded-xl border border-border p-4 text-xs font-medium hover:bg-secondary"
                >
                  <item.icon className="w-5 h-5 text-muted-foreground" />
                  {item.label}
                  {item.to === "/content" && contentBadge("absolute top-1.5 right-1.5")}
                  {item.to === "/receipts" && receiptsBadge("absolute top-1.5 right-1.5")}
                  {item.to === "/admin/access" && accessBadge("absolute top-1.5 right-1.5")}
                </NavLink>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
};
